import {
    LightComponent,
    MeshComponent,
    Object3D,
    PhysXComponent,
} from '@wonderlandengine/api';
import {vec3} from 'gl-matrix';
import {delay} from '../utils/delay.js';
import {hapticFeedback} from '../utils/haptic-feedback.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {Cursor, CursorTarget} from '@wonderlandengine/components';
import {Inventory} from './inventory.js';
import {InspectWorldObjectOffsets} from './inspect-world-object-offsets.js';
import {ActiveCamera} from './active-camera.js';
import {CrimeDoc} from './crime-doc.js';
import {Newspaper} from './newspaper.js';
import {PhongMaterial} from '../types/materials.js';
import {CursorReticle, CursorType} from './cursor-reticle.js';
import {InspectNote} from './inspect-note.js';
import {TextBubbles} from './text-bubbles.js';
import {WasdControlsFps} from './wasd-controls-fps.js';
import {LevelManagerScene2} from '../scene2/level-manager-scene2.js';
import {Blink} from './blink.js';
import {Teleport} from './teleport.js';
import {Inspect} from './inspect.js';

/**
 * inspect-world-object
 */
export class InspectWorldObject extends Inspect {
    static TypeName = 'inspect-world-object';
    static InheritProperties = true;
    /* Properties that are configurable in the editor */

    /* General */
    @property.object()
    levelManager?: Object3D;

    @property.object()
    inspectSphere?: Object3D; /* The sphere that will be closed on us, exists in Player's child */
    @property.object()
    inspectLight?: Object3D;
    @property.object()
    textObj?: Object3D; /* Text Bubble */
    @property.object()
    objectHover?: Object3D; /* Object Mesh Hover to Highlight, Usually Self or a Child Mesh */
    @property.bool(true)
    enableRotObj = true; /* Enable Object Rotation? */
    @property.bool(false)
    grabbable =
        false; /* Is Object grabbable to hand before inspecting? If true they also dissappear to inventory after */
    @property.object()
    locGrabbed?: Object3D; /* The location where grabbed object will lerp to if not in VR */
    @property.float(0.5)
    grabSpeed = 0.5;
    @property.float(1)
    delayAfterGrab = 1;

    /* Note */
    @property.bool(false)
    transitionToNote = false; /* Transition to Note after inspecting? */
    @property.object()
    noteObj?: Object3D;

    /* CrimeDoc */
    @property.bool(false)
    isCrimeDoc = false; /* Transition to CrimeDoc? */
    @property.object()
    crimeDocObj?: Object3D; /* Crime Book Obj */

    /* Newspaper */
    @property.bool(false)
    isNewspaper = false; /* Transition to Newspaper? */
    @property.object()
    newspaperObj?: Object3D; /* Newspaper Obj */

    // playerTransform = vec3.create();
    noteObjTransform = vec3.create();

    private inspectedObj?: Object3D;
    private objOffsets?: InspectWorldObjectOffsets | null;
    private rotateObjAxis = vec3.create();
    private textObjRotOffsetX = 0;
    private textObjRotOffsetY = 0;
    private textObjRotOffsetZ = 0;
    private textObjDistance = 1.3;
    private textObjHeight = 0;
    private inspectedObjDistance = 0;
    private inspectedObjRotOffsetX = 0;
    private inspectedObjRotOffsetY = 0;
    private inspectedObjRotOffsetZ = 0;
    private inspectedObjPosition = vec3.create();
    private headObject?: Object3D;
    private rotateObj = false;
    private inspecting = false;
    private inspectedObjTransform = vec3.create();
    private lerpTime = 0;
    private inspectGate = false;
    private lerpRatio = 0;
    private handPosition = vec3.create();
    private lerpResult = vec3.create();

    private playerPhysXComponent?: PhysXComponent;
    private playerWasdControlsFps?: WasdControlsFps;
    private inspectSphereMesh?: MeshComponent;
    private inspectLightLight?: LightComponent;
    private levelManagerScene2?: LevelManagerScene2;
    private blink?: Blink;
    private teleport?: Teleport;

    start() {
        /* Inspected Object, was especially made for CrimeDoc */
        this.inspectedObj = this.crimeDocObj || this.newspaperObj || this.object;

        /* Add Cursor Functions */
        const target =
            this.object.getComponent(CursorTarget) ||
            this.object.addComponent(CursorTarget)!;

        target.onHover.add(this.onHover.bind(this));
        target.onUnhover.add(this.onUnHover.bind(this));
        target.onDown.add(() => {
            this.onDown().catch(console.error);
        });

        /* Offsets Properties extension, add 'inspect-world-object-offsets' component to enable it */
        this.objOffsets = this.object.getComponent(InspectWorldObjectOffsets);
        this.rotateObjAxis = this.objOffsets?.getRotateObjAxis() ?? [0, 0, 1];

        this.textObjRotOffsetX = this.objOffsets?.textObjRotOffsetX ?? 0;
        this.textObjRotOffsetY = this.objOffsets?.textObjRotOffsetY ?? 0;
        this.textObjRotOffsetZ = this.objOffsets?.textObjRotOffsetZ ?? 0;
        this.textObjDistance = this.objOffsets?.textObjDistance ?? 1.3;
        this.textObjHeight = this.objOffsets?.textObjHeight ?? 0.3;
        this.inspectedObjDistance = this.objOffsets?.inspectedObjDistance ?? 1.3;
        if (!this.engine.xr) {
            /* Desktop needs object much closer to be readible */
            this.inspectedObjDistance *= 0.5;
        }
        this.inspectedObjRotOffsetX = this.objOffsets?.inspectedObjRotOffsetX ?? 0;
        this.inspectedObjRotOffsetY = this.objOffsets?.inspectedObjRotOffsetY ?? 0;
        this.inspectedObjRotOffsetZ = this.objOffsets?.inspectedObjRotOffsetZ ?? 0;

        /* Clone objectHover material, to only affect this object's material */
        if (!this.objectHover) {
            throw new Error(
                `inspect-world-object(${this.object.name}): objectHover is not defined`
            );
        }
        this.objectHover.getComponents(MeshComponent).forEach((meshComponent) => {
            if (meshComponent.material) {
                meshComponent.material = meshComponent.material.clone();
            }
        });

        /* General */
        if (!this.player) {
            throw new Error(
                `inspect-world-object(${this.object.name}): player is not defined`
            );
        }
        const tempTeleport = this.player.getComponent(Teleport);
        if (!tempTeleport) {
            throw new Error(
                `inspect-world-object(${this.object.name}): player does not have a Teleport component`
            );
        }
        this.teleport = tempTeleport;

        const activeCameraComponent = this.player.getComponent(ActiveCamera);
        if (!activeCameraComponent) {
            throw new Error(
                `inspect-world-object(${this.object.name}): player does not have an ActiveCamera component`
            );
        }
        const tempBlink = this.player.getComponent(Blink);
        if (!tempBlink) {
            throw new Error(
                `inspect-world-object(${this.object.name}): player does not have a Blink component`
            );
        }
        this.blink = tempBlink;

        const tempPhysxComponent = this.player.getComponent(PhysXComponent);
        if (!tempPhysxComponent) {
            throw new Error(
                `inspect-world-object(${this.object.name}): player does not have a PhysXComponent component`
            );
        }
        this.playerPhysXComponent = tempPhysxComponent;

        const tempWasdControlsFps = this.player.getComponent(WasdControlsFps);
        if (!tempWasdControlsFps) {
            throw new Error(
                `inspect-world-object(${this.object.name}): player does not have a WasdControlsFps component`
            );
        }
        this.playerWasdControlsFps = tempWasdControlsFps;

        this.headObject = activeCameraComponent.getActiveCamera();

        this.rotateObj = false;

        if (!this.inspectSphere) {
            throw new Error(
                `inspect-world-object(${this.object.name}): inspectSphere is not defined`
            );
        }
        this.inspectLight = this.inspectSphere.children[0];
        this.inspecting = false;

        if (!this.locInspectedObj) {
            throw new Error(
                `inspect-world-object(${this.object.name}): locInspectedObj is not defined`
            );
        }

        const tempInspectSphereMesh = this.inspectSphere.getComponent(MeshComponent);
        if (!tempInspectSphereMesh) {
            throw new Error(
                `inspect-note(${this.object.name}): inspectSphere does not have a MeshComponent component`
            );
        }
        this.inspectSphereMesh = tempInspectSphereMesh;

        const tempInspectLightLight = this.inspectLight.getComponent(LightComponent);
        if (!tempInspectLightLight) {
            throw new Error(
                `inspect-note(${this.object.name}): inspectLight does not have a LightComponent component`
            );
        }
        this.inspectLightLight = tempInspectLightLight;

        /* Store Transform */
        this.inspectedObjTransform = this.inspectedObj.getTransformWorld();

        /* Lerp Grab */
        this.lerpTime = 100;

        /* Gates */
        this.inspectGate = false;

        /* CrimeDoc */
        if (this.isCrimeDoc) {
            if (!this.crimeDocObj) {
                throw new Error(
                    `inspect-world-object(${this.object.name}): crimeDocObj is not defined`
                );
            }
            const crimeDoc = this.crimeDocObj.getComponent(CrimeDoc);
            if (!crimeDoc) {
                throw new Error(
                    `inspect-world-object(${this.object.name}): crimeDocObj does not have a CrimeDoc component`
                );
            }
            crimeDoc.info(this.object);
        }

        /* Newspaper */
        if (this.isNewspaper) {
            if (!this.newspaperObj) {
                throw new Error(
                    `inspect-world-object(${this.object.name}): newspaperObj is not defined`
                );
            }
            const newspaperComponent = this.newspaperObj.getComponent(Newspaper);
            if (!newspaperComponent) {
                throw new Error(
                    `inspect-world-object(${this.object.name}): newspaperObj does not have a Newspaper component`
                );
            }
            newspaperComponent.info(this.object);
        }

        if (!this.locGrabbed) {
            throw new Error(
                `inspect-world-object(${this.object.name}): locGrabbed is not defined`
            );
        }
    }

    update(dt: number) {
        /* Rotate Obj */
        if (this.rotateObj && this.enableRotObj) {
            this.inspectedObj!.rotateAxisAngleDegObject(this.rotateObjAxis, 50 * dt);
        }

        /* Lerp to Hand Grab */
        this.lerpTime += dt;
        this.lerpRatio = this.lerpTime / this.grabSpeed; /* Seconds */
        if (this.lerpTime < this.grabSpeed) {
            this.handPosition = this.locGrabbed!.getPositionWorld();
            this.lerpResult = vec3.lerp(
                vec3.create(),
                this.inspectedObjPosition,
                this.handPosition,
                this.lerpRatio
            );
            this.object.setPositionWorld(this.lerpResult);
        } else if (this.inspectGate) {
            this.object.setPositionWorld(
                this.locGrabbed!.getPositionWorld()
            ); /* Attach objectPos to handPos till blinking */
        }
    }

    onActivate(): void {
        this.engine.onXRSessionStart.add(this.onSessionStart.bind(this));
        this.engine.onXRSessionEnd.add(this.onSessionEnd.bind(this));
    }
    onDeactivate(): void {
        this.engine.onXRSessionStart.remove(this.onSessionStart.bind(this));
        this.engine.onXRSessionEnd.remove(this.onSessionEnd.bind(this));
    }

    onSessionStart() {
        /* Reset headObject to leftEye, to calculate inspectedLoc accordingly in VR */
        //Bug: It's not working
        this.headObject = this.player!.getComponent(ActiveCamera)!.getActiveCamera();
    }

    onSessionEnd() {
        /* Reset headObject to NonVR, to calculate inspectedLoc accordingly in NonVR */
        //Bug: It's not working
        this.headObject = this.player!.getComponent(ActiveCamera)!.getActiveCamera();
    }

    onHover(_: Object3D, cursor: Cursor) {
        if (this.inspecting) {
            return;
        }
        hapticFeedback(cursor.object, 0.5, 50);
        /* Highlight */
        this.objectHover!.getComponents(MeshComponent).forEach((meshComponent) => {
            const material = meshComponent.material as PhongMaterial;
            //a.material.ambientColor = [0.5, 0.5, 0.5, 1];
            material.emissiveColor = [0.12, 0.12, 0.12, 1]; //Some materials' pipeline are "Physical" and have emissive instead of ambient.
        });

        /* Cursor */
        const cursorReticleComponent = this.player.getComponent(CursorReticle);
        if (!cursorReticleComponent) {
            throw new Error(
                `inspect-world-object(${this.object.name}): player does not have a CursorReticle component`
            );
        }
        cursorReticleComponent.changeCursorTexture(cursor, CursorType.Magnify);
    }

    onUnHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.3, 50);
        /* Highlight Reset to Default */
        this.objectHover!.getComponents(MeshComponent).forEach((meshComponent) => {
            const material = meshComponent.material as PhongMaterial;
            //a.material.ambientColor = [0.050, 0.050, 0.050, 1];
            material.emissiveColor = [0, 0, 0, 1];
        });

        /* Cursor */
        const cursorReticleComponent = this.player.getComponent(CursorReticle);
        if (!cursorReticleComponent) {
            throw new Error(
                `inspect-world-object(${this.object.name}): player does not have a CursorReticle component`
            );
        }
        cursorReticleComponent.changeCursorTexture(cursor, CursorType.Default);
    }

    async onDown() {
        if (this.inspectGate) {
            return; /* If in the middle of startInspecting then don't continue */
        }
        if (this.inspecting) {
            if (this.transitionToNote) {
                /* Store Note Transform */
                if (this.noteObj) {
                    this.noteObjTransform = this.noteObj.getTransformWorld();

                    const inspectNoteComponent = this.noteObj.getComponent(InspectNote);
                    if (!inspectNoteComponent) {
                        console.error(
                            `inspect-world-object(${this.object.name}): noteObj does not have an InspectNote component`
                        );
                        return;
                    }
                    /* Start Note & Reset this Inspected Object without reseting Inspect World */
                    this.resetObjOnly();
                    inspectNoteComponent.startNote(this.object, true);
                } else {
                    console.error(
                        `inspect-world-object(${this.object.name}): noteObj is not referenced`
                    );
                }
            } else if (this.textObj) {
                const textBubbles = this.object.getComponent(TextBubbles);
                if (!textBubbles) {
                    console.error(
                        `inspect-world-object(${this.object.name}): object does not have a TextBubbles component`
                    );
                    return;
                }
                /* If Still writing, then finish Writing first */
                if (textBubbles.displaying == true) {
                    textBubbles.delayLetters = 0;
                    await delay(0.1); /* Wait letters to be written */
                    textBubbles.displaying = false;
                } else {
                    textBubbles.delayLetters = textBubbles.delayLettersDefault;
                    this.resetSelf();
                    this.inspecting = !this.inspecting; /* toggle */
                }
            } else {
                this.resetSelf();
                this.inspecting = !this.inspecting; /* toggle */
            }
        } else {
            this.inspectedObjPosition =
                this.object.getPositionWorld(); /* Store Transform */
            this.inventoryAdd();
            await this.startInspect();
            this.inspecting = !this.inspecting; /* toggle */
        }
    }

    inventoryAdd() {
        /* CrimeDoc Inventory */
        if (this.isCrimeDoc) {
            const playerInventory = this.player!.getComponent(Inventory);
            if (!playerInventory) {
                console.error(
                    'inspect-world-object: Player does not have an inventory component'
                );
                return;
            }

            playerInventory.crimeDocNum += 1; /* Increase crimeDocNum */
            if (!this.levelManager) {
                console.error(
                    `inspect-world-object(${this.object.name}): levelManager is not referenced`
                );
                return;
            }
            const tempLevelManager = this.levelManager.getComponent(LevelManagerScene2);
            if (!tempLevelManager) {
                console.error(
                    `inspect-world-object(${this.object.name}): levelManager does not have a LevelManagerScene2 component`
                );
                return;
            }
            this.levelManagerScene2 = tempLevelManager; /* Reference levelManager */

            this.levelManagerScene2.checkCrimeDocNum(
                this.object,
                playerInventory.crimeDocNum
            ); /* send crimeDocNum to levelManager to check it */
        }
    }

    async startInspect() {
        /* Gate Close */
        this.inspectGate = true;

        /* Grab */
        if (this.grabbable) {
            this.lerpTime = 0; /* Start Lerp */
            await delay(this.delayAfterGrab); /* Wait till grab */
        }

        /* Blink & Change Locations of Inspected Obj & Player */
        this.blink!.startBlink(() => {
            this.teleport!.teleport([30, 0, 0]);

            this.playerWasdControlsFps!.active = false; /* Disable Movement */
            this.playerPhysXComponent!.active = false; /* Disable Movement */
            //this.object.setTranslationWorld([31, 1, 0]);
            //this.playerRotQuat = quat.fromEuler(quat.create(), 0, -90, 0);
            //this.player.setRotationWorld(this.playerRotQuat);

            /* Inspected Object Set Location & Start Rotation */
            this.setTransformInfrontOf(
                this.player,
                this.inspectedObj!,
                this.inspectedObjDistance,
                0,
                [
                    this.inspectedObjRotOffsetX,
                    this.inspectedObjRotOffsetY,
                    this.inspectedObjRotOffsetZ,
                ]
            );
            this.rotateObj = true;

            /* Inspected Sphere & Light, Activate & Set Locations */
            this.inspectSphereMesh!.active = true;
            /* Inspected Light Transform */
            this.inspectLightLight!.active = true;
            this.setTransformInfrontOf(this.player, this.inspectLight!, 5);

            /* Text Bubble */
            if (this.textObj != null) {
                this.setTransformInfrontOf(
                    this.headObject!,
                    this.textObj,
                    this.textObjDistance,
                    this.textObjHeight,
                    [this.textObjRotOffsetX, this.textObjRotOffsetY, this.textObjRotOffsetZ]
                );
                const textBubbles = this.object.getComponent(TextBubbles);
                if (!textBubbles) {
                    console.error(
                        `inspect-world-object(${this.object.name}): object does not have a TextBubbles component`
                    );
                    return;
                }
                textBubbles.active = true;
                textBubbles.displayNextLetter();
            }

            /* Gate Close */
            this.inspectGate = false;
        }).catch(console.error);
        /* ToDo:
         *Set Object LookAt [ok]
         *Rotate Object [ok]
         *Blue Sphere World, with Light [ok]
         *Start BubbleText up it [ok][Bug: Need Rot & Loc Offset]
         *Disable Player Movement [ok]
         *Disable Object Collision [ok]
         *Reset Locations, Movement and Collisions [ok]
         */
    }

    resetSelf() {
        /* Blink & Change Locations of Inspected Obj & Player */
        //this.blink!.startBlink().catch(console.error);
        this.teleport!.back();
        this.inspectedObj!.setTransformWorld(this.inspectedObjTransform);

        /* Stop Rotation, & Disable InspectSphere */
        this.rotateObj = false;
        this.inspectSphereMesh!.active = false;
        this.inspectLightLight!.active = false;

        /* Re-enable Movement */
        this.playerWasdControlsFps!.active = true;

        /* Text Bubble Reset */
        if (this.textObj != null) {
            const textBubbles = this.object.getComponent(TextBubbles);
            if (!textBubbles) {
                console.error(
                    `inspect-world-object(${this.object.name}): object does not have a TextBubbles component`
                );
                return this;
            }
            textBubbles.resetSelf();
        }

        /* Note Reset */
        //this.noteGate = false;
        if (this.transitionToNote) {
            if (!this.noteObj) {
                console.error(
                    `inspect-world-object(${this.object.name}): noteObj is not referenced`
                );
                return this;
            }
            this.noteObj.setTransformWorld(this.noteObjTransform);
        }

        const objectHover = this.objectHover;
        if (!objectHover) {
            console.error(
                `inspect-world-object(${this.object.name}): objectHover is not referenced`
            );
            return this;
        }
        objectHover.getComponents(MeshComponent).forEach((a) => {
            a.active = true; /* Unhide */
        });

        return this;
    }

    resetObjOnly() {
        /* Blink & Change Locations of Inspected Obj */
        //await this.blink!.startBlink();
        this.inspectedObj!.setTransformWorld(this.inspectedObjTransform);
        /* Stop Rotation, & Hide textObj */
        this.rotateObj = false;
        this.textObj?.setPositionWorld([30, -30, 0]); /* Hide textObj */
    }
}
